import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import Reveal from "../components/site/Reveal";
import { SERVICE_DETAIL, IMAGES, BOOKING_URL } from "../lib/site";
import { ArrowUpRight } from "lucide-react";
import { useSeo } from "../lib/seo";

const EASE = [0.22, 1, 0.36, 1];

const containerStagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06, delayChildren: 0.05 } },
};

const lineMask = {
  hidden: { y: "110%" },
  visible: { y: "0%", transition: { duration: 0.9, ease: EASE } },
};

const softRise = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 1.0, ease: EASE } },
};

export default function Services() {
  const location = useLocation();
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll();
  const heroImgY = useTransform(scrollYProgress, [0, 0.25], ["0%", "10%"]);
  const heroImgScale = useTransform(scrollYProgress, [0, 0.25], [1.02, 1.06]);

  useSeo({
    title:
      "Dental Services in Woodbridge | Alae Dental. Invisalign, Implants and More",
    description:
      "Comprehensive dental care in Vaughan: preventive, restorative, cosmetic, Invisalign, implants & emergency dentistry. All services provided by General Dentists.",
    path: "/services",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "MedicalBusiness",
      name: "Alae Dental. Services",
      url: "https://www.alaedental.com/services",
      areaServed: [
        { "@type": "City", name: "Vaughan" },
        { "@type": "City", name: "Woodbridge" },
      ],
      medicalSpecialty: "Dentistry",
      availableService: SERVICE_DETAIL.flatMap((cat) =>
        cat.items.map((it) => ({
          "@type": "MedicalProcedure",
          name: it.name,
          description: it.body,
          category: cat.title,
        }))
      ),
    },
    jsonLdId: "services-jsonld",
  });

  // Scroll to hash anchor (deep-link from homepage cards)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const el = document.getElementById(id);
      if (el) {
        setTimeout(
          () => el.scrollIntoView({ behavior: "smooth", block: "start" }),
          80
        );
      }
    }
  }, [location.hash]);

  return (
    <main className="bg-linen text-umber">
      {/* Kinetic Hero */}
      <section className="relative overflow-hidden paper-grain min-h-[66svh] flex items-end pt-24 md:pt-32 pb-16 md:pb-24 bg-paper">
        <motion.div
          className="absolute inset-0 opacity-40"
          style={reduce ? undefined : { y: heroImgY, scale: heroImgScale }}
          initial={reduce ? false : { scale: 1.1, opacity: 0 }}
          animate={reduce ? undefined : { scale: 1.02, opacity: 0.4 }}
          transition={{ duration: 1.6, ease: EASE }}
        >
          <img
            src={IMAGES.servicesAccent}
            alt=""
            aria-hidden="true"
            fetchPriority="high"
            decoding="sync"
            className="absolute inset-0 w-full h-[115%] object-cover"
            style={{ filter: "saturate(0.85) contrast(0.95)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-paper/60 via-paper/30 to-paper/80" />
        </motion.div>

        <motion.div
          className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 lg:px-16 w-full"
          variants={containerStagger}
          initial="hidden"
          animate="visible"
        >
          <div className="flex items-center gap-4 mb-6">
            <motion.div
              className="h-[1px] w-10 md:w-14 bg-forest origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 1.1, ease: EASE, delay: 0.1 }}
            />
            <motion.div variants={softRise} className="font-meta text-forest">
              Services
            </motion.div>
          </div>

          <h1 className="font-display text-5xl md:text-6xl lg:text-[80px] leading-[1.12] md:leading-[1.06] tracking-tight text-umber max-w-4xl">
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                Comprehensive care,
              </motion.span>
            </span>
            <span className="block overflow-hidden">
              <motion.span variants={lineMask} className="block">
                <span className="font-display-italic text-forest">
                  unhurried.
                </span>
              </motion.span>
            </span>
          </h1>

          <motion.p
            variants={softRise}
            className="mt-8 text-base md:text-lg leading-relaxed text-umber/85 max-w-2xl"
          >
            Comprehensive, modern dental care for every stage of life,
            delivered with unhurried attention.
          </motion.p>

          <motion.p
            variants={softRise}
            data-testid="services-provider-disclaimer"
            className="mt-5 text-sm leading-relaxed text-umber/65 max-w-2xl"
          >
            All dental services and treatments at Alae Dental are provided by
            General Dentists.
          </motion.p>

          <motion.div variants={softRise} className="mt-9">
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="services-hero-book"
              className="inline-flex items-center gap-3 bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-colors duration-300 min-h-[44px]"
            >
              Book an Appointment <ArrowUpRight size={16} />
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* Sticky index */}
      <section className="bg-linen border-y border-umber/15 sticky top-16 md:top-20 z-30 backdrop-blur-md bg-linen/85">
        <div className="relative">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16 py-4 overflow-x-auto">
            <ul className="flex gap-8 md:gap-12 whitespace-nowrap text-[11px] tracking-[0.22em] uppercase text-umber/75 pr-10">
              {SERVICE_DETAIL.map((s) => (
                <li key={s.key}>
                  <a href={`#${s.key}`} className="hover:text-forest transition-colors">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
          <div
            aria-hidden="true"
            className="pointer-events-none absolute top-0 right-0 h-full w-14 bg-gradient-to-l from-linen to-transparent md:hidden"
          />
        </div>
      </section>

      {/* Category sections */}
      {SERVICE_DETAIL.map((cat, idx) => (
        <section
          key={cat.key}
          id={cat.key}
          className={`paper-grain py-24 md:py-32 ${
            idx % 2 === 0 ? "bg-linen" : "bg-paper"
          }`}
        >
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
              <div className="lg:col-span-6">
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    className="h-[1px] w-10 md:w-14 bg-forest origin-left"
                    initial={{ scaleX: 0 }}
                    whileInView={{ scaleX: 1 }}
                    viewport={{ once: true, amount: 0.6 }}
                    transition={{ duration: 1.1, ease: EASE }}
                  />
                  <div className="font-meta text-forest">Part 0{idx + 1}</div>
                </div>
                <motion.h2
                  className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.14] md:leading-[1.08] tracking-tight text-umber overflow-hidden"
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, amount: 0.4 }}
                  variants={containerStagger}
                >
                  <motion.span variants={lineMask} className="block">
                    {cat.title}
                  </motion.span>
                </motion.h2>
              </div>
              <div className="lg:col-span-6 lg:pt-8">
                <Reveal delay={200}>
                  <p className="font-display-italic text-xl md:text-2xl leading-snug text-umber/80">
                    {cat.tagline}
                  </p>
                </Reveal>
              </div>
            </div>

            <div className="border-t border-umber/20">
              {cat.items.map((it, i) => (
                <motion.div
                  key={it.name}
                  data-testid={`service-item-${cat.key}-${i}`}
                  initial={reduce ? false : { opacity: 0, y: 26 }}
                  whileInView={reduce ? undefined : { opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.25 }}
                  transition={{ duration: 0.9, ease: EASE, delay: (i % 3) * 0.08 }}
                  className="group grid grid-cols-12 gap-6 border-b border-umber/20 py-8 md:py-10 transition-colors duration-500 hover:bg-umber/[0.03]"
                >
                  <div className="col-span-12 md:col-span-5">
                    <h3 className="font-display text-2xl md:text-3xl text-umber group-hover:text-forest transition-colors duration-300">
                      {it.name}
                    </h3>
                    <span className="mt-3 block h-[1px] w-0 bg-forest transition-all duration-500 group-hover:w-16" />
                  </div>
                  <div className="col-span-12 md:col-span-7 text-umber/85 leading-relaxed text-base md:text-[17px]">
                    {it.body}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="relative py-24 md:py-32 paper-grain overflow-hidden">
        <div className="absolute inset-0 opacity-30">
          {/* PLACEHOLDER: replace with real photography */}
          <img
            src={IMAGES.waitlist}
            alt=""
            aria-hidden="true"
            loading="lazy"
            decoding="async"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-linen/60" />
        </div>
        <div className="relative max-w-4xl mx-auto px-6 md:px-12 lg:px-16 text-center">
          <Reveal>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl leading-[1.06] tracking-tight text-umber">
              Ready to begin?
            </h2>
          </Reveal>
          <Reveal delay={150}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 mt-10 bg-forest text-paper rounded-full px-9 py-4 text-[12px] tracking-[0.22em] uppercase hover:bg-sage transition-all duration-300"
            >
              Book an Appointment <ArrowUpRight size={16} />
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
